import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Resource, ResourceType, ResourceUtils} from "../../models/resource";
import {Paginator} from "../../models/paginator";
import {UiManagerService} from "../../services/ui-manager.service";
import {ResourceManager} from "../../services/resource-manager";
import {Equipment} from "../../models/equipment";
import {Metadata} from "../../models/metadata";
import {SettingsManagerService} from "../../services/settings-manager.service";
import {environment} from "@environments/environment";
import {MatDialog} from "@angular/material/dialog";
import {
  EditResourceDialogComponent,
  EditResourceDialogData
} from "../dialogs/edit-resource-dialog/edit-resource-dialog.component";
import {TranslatePipe} from "@ngx-translate/core";

// TODO : generalize this component to list procedures
@Component({
  selector: 'app-media-grid',
  templateUrl: './media-grid.component.html',
  styleUrls: ['./media-grid.component.scss']
})
export class MediaGridComponent implements OnInit {

  isUploading: boolean;

  @Input()
  equipment: Equipment;

  @Input()
  containerHeight: number | 'fill';

  @Input()
  rows: number;

  @Input()
  cols: number;

  @Input()
  fixDimension: 'columns' | 'rows';

  @Input()
  paginator: Paginator<Resource>;

  @Input()
  resources: Resource[];

  @Input()
  editable: boolean;

  @Input()
  selectable: boolean;

  @Input()
  multiselect: boolean;

  @Input()
  searchBarExpanded: boolean;

  @Input()
  uploadableResources: ResourceType[];

  // TODO use proper filtering: using API filtering and not client side filtering
  @Input()
  viewableResources: ResourceType[];

  @Output()
  onResourceSelected: EventEmitter<Resource>;

  @Output()
  onResourceUploaded: EventEmitter<Resource>;

  selectedResources: string[];

  constructor(
    private uiManagerService: UiManagerService,
    private settingsManagerService: SettingsManagerService,
    private resourceManager: ResourceManager,
    private dialog: MatDialog,
    private translatePipe: TranslatePipe
  ) {
    this.onResourceSelected = new EventEmitter<Resource>();
    this.onResourceUploaded = new EventEmitter<Resource>();
  }

  ngOnInit(): void {
    this.selectedResources = [];
    this.uploadableResources = this.uploadableResources || [];

    // Compute rows or columns based on columns or rows respectively
    switch (this.fixDimension) {
      case "columns":
        this.rows = Math.ceil(this.paginator.pageSize / this.cols);
        break;
      case "rows":
        this.cols = Math.ceil(this.paginator.pageSize / this.rows);
        break;
    }

  }

  openResource(resource: Resource) {
    if (resource.url) {
      window.open(resource.url, '_blank');
    } else {
      this.uiManagerService.notify(this.translatePipe.transform('An error occurred. Try again'));
    }
  }

  edit(resource: Resource) {
    this.resourceManager.editResource(resource, this.uploadableResources);
  }

  remove(resource: Resource) {
    this.uiManagerService.showConfirmDialog('Remove resource', 'Are you sure do you want to remove this resource?<br>This operation is irreversible.',
      () => {
        this.resourceManager.removeResource(resource);
        this.resources.splice(this.resources.indexOf(resource), 1);
      }
    );
  }

  selectResource(resource: Resource) {

    // Check if disabled
    if (this.isResourceDisabled(resource.type)) {
      return;
    }

    if (!resource.url) {
      this.uiManagerService.notify(this.translatePipe.transform('An error occurred. Try again'));
      return;
    }
    if (this.multiselect) {

      if (this.selectedResources.includes(resource.id)) {
        // Remove selection
        this.selectedResources.splice(this.selectedResources.indexOf(resource.id), 1);
      } else {
        // Add selection
        this.selectedResources.push(resource.id);
      }

    } else {

      if (this.selectedResources.length == 0) {
        this.selectedResources.push(resource.id);
      } else {
        this.selectedResources[0] = resource.id;
      }

    }
    this.onResourceSelected.emit(resource);
  }

  clearSelection() {
    this.selectedResources = [];
  }

  uploadResource() {

    const dialogRef = this.dialog.open(EditResourceDialogComponent, {
      width: '350px',
      data: {
        isNew: true,
        name: '',
        tags: [],
        uploadableResources: this.uploadableResources,
      } as EditResourceDialogData,
    });

    dialogRef.afterClosed().subscribe(async (value: EditResourceDialogData) => {
      if (value) {

        this.isUploading = true;

        // Upload resource
        const resource = await this.resourceManager.uploadResource(this.equipment, value.file, value.tags, value.name);

        // Set resource metadata
        const metadata = new Metadata();
        metadata.size = value.file.size;
        metadata.contentType = ResourceUtils.FileToContentType(value.file);

        // Set video duration if type is video
        if (resource.type == ResourceType.VIDEO) {

          this.getVideoDuration(value.file, async duration => {
            metadata.duration = duration;
            await this.finishResourceUploading(resource, metadata);
          });

        } else {
          await this.finishResourceUploading(resource, metadata);
        }

      }
    });

    // TODO DEPRECATED
    /*const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = ResourceUtils.ResourceTypesToContentType(this.uploadableResources).replace('model/gltf-binary', '.gltf,.glb'); // Fix required to get right files

    fileInput.onchange = async e => {
      const file = e.target['files'][0];

      // Check upload size
      const { size, type } = ResourceUtils.GetFileMetadata(file);
      if (
        (type == ResourceType.VIDEO && size > await this.settingsManagerService.getVideoMaxUploadSize()) ||
        (type != ResourceType.VIDEO && size > await this.settingsManagerService.getFileMaxUploadSize())
      ) {
        if (type == ResourceType.VIDEO) {
          this.uiManagerService.showInfoDialog('Resource upload',  `Max upload size reached: ${await this.settingsManagerService.getVideoMaxUploadSize() / 1024 / 1024}MB`);
        } else {
          this.uiManagerService.showInfoDialog('Resource upload',  `Max upload size reached: ${await this.settingsManagerService.getFileMaxUploadSize() / 1024 / 1024}MB`);
        }
        return;
      }

      this.isUploading = true;

      // Upload resource
      const resource = await this.resourceManager.uploadResource(this.equipment, file);

      // Set resource metadata
      const metadata = new Metadata();
      metadata.size = file.size;
      metadata.contentType = ResourceUtils.FileToContentType(file);

      // Set video duration if type is video
      if (resource.type == ResourceType.VIDEO) {

        this.getVideoDuration(file, async duration => {
          metadata.duration = duration;
          await this.finishResourceUploading(resource, metadata);
        });

      } else {
        await this.finishResourceUploading(resource, metadata);
      }

    }
    fileInput.click();*/

  }

  private async finishResourceUploading(resource: Resource, metadata: Metadata) {

    // Set resource metadata
    await this.resourceManager.setResourceMetadata(resource, metadata);

    // Reload list
    this.isUploading = false;
    await this.paginator.refresh();

  }

  getVideoDuration(videoFile: File, callback: (duration: number) => void) {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = function() {
      window.URL.revokeObjectURL(video.src);
      callback(video.duration);
    }
    video.src = URL.createObjectURL(videoFile);
  }

  isResourceDisabled(resourceType: ResourceType) {
    return this.viewableResources && !this.viewableResources.includes(resourceType);
  }

  getBottomIcon(resource: ResourceType) {
    switch (resource) {
      case ResourceType.VIDEO:
        return 'play_circle_filled';
      case ResourceType.IMAGE:
        return 'image';
      case ResourceType.DOC:
        return 'insert_drive_file';
      case ResourceType.MODEL:
        return '3d_rotation';
    }
  }

  getBottomIconLabel(resource: ResourceType) {
    switch (resource) {
      case ResourceType.VIDEO:
        return 'Play video';
      case ResourceType.IMAGE:
        return 'Open image';
      case ResourceType.DOC:
        return 'Open document';
      case ResourceType.MODEL:
        return 'Download model';
    }
  }

  getImagePreviewPlaceholder(resource: ResourceType) {
    switch (resource) {
      case ResourceType.VIDEO:
        return 'assets/img/resources/video.png';
      case ResourceType.IMAGE:
        return 'assets/img/resources/image.png';
      case ResourceType.DOC:
        return 'assets/img/resources/document.png';
      case ResourceType.MODEL:
        return 'assets/img/resources/model.png';
    }
  }

  filterResources(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  getGridColumnSize() {
    return this.fixDimension == 'columns' ? (100 / this.cols + '%') : '400px';
  }

  get environment() { return environment; }

}
